/* eslint-disable */

// NOTE ON TERMINOLOGY
// An "opportunity" is NOT a separate pipeline — it is the CARD that sits inside a
// status of a pipeline, linked to a client. Renewals hold opportunities for existing
// clients (expiring certificates); New Deals hold opportunities coming from leads
// (once one produces a certificate the lead becomes our customer); Tickets hold the
// daily client requests.

export const OPPORTUNITY_TYPES = ['new', 'renewal', 'extend', 'survey']

// Karim, 2 Sep: "in renewal I need to define won and lost." A single 'Completed'
// column ("Decision Made") could not tell a renewed client from a rejected one
// without opening the card — matching Deals' own New -> ... -> Won / Lost split,
// with the wording he set on Aug 16: an EXISTING client who declines is REJECTED,
// not "lost" (that word is reserved for a NEW lead who never bought). The id
// values MUST match CrmPipelineController::applyDecision()'s decision.status
// ('renewed' / 'not_renewed') — that is what a card's stage becomes once the
// outcome is logged, on both the bulk-action path and the single-card decision
// modal (PipelineCRM.vue submitDecision).
export const RENEWAL_STAGES = [
  { id: 'pending', name: 'Pending Renewal', color: '#f59e0b', wipLimit: 0, description: 'Certificates nearing expiration date' },
  { id: 'calling', name: 'Calling Client', color: '#6366f1', wipLimit: 0, description: 'Initial contact made by salesperson' },
  { id: 'emailing', name: 'Sending Email', color: '#3b82f6', wipLimit: 0, description: 'Quotation sent via email' },
  { id: 'negotiation', name: 'Under Negotiation', color: '#a855f7', wipLimit: 0, description: 'Price/terms under negotiation' },
  { id: 'renewed', name: 'Renewed', color: '#10b981', wipLimit: 0, description: 'Client renewed — certificate to be generated' },
  { id: 'not_renewed', name: 'Rejected', color: '#ef4444', wipLimit: 0, description: 'Client declined to renew — reason required' }
]

// New Deals — opportunities that came from the leads list. Each status holds
// opportunities; each status has its own sub-steps.
export const DEAL_STAGES_DEFAULT = [
  { id: 'new', name: 'New', color: '#6366f1', wipLimit: 0, description: 'Opportunity created from a lead', subStages: ['Qualify need', 'Select service / product', 'Assign owner'] },
  { id: 'proposal', name: 'Proposal Sent', color: '#3b82f6', wipLimit: 0, description: 'Proposal sent to the client', subStages: ['Prepare proposal', 'Send proposal', 'Confirm receipt'] },
  { id: 'responded', name: 'Responded', color: '#f59e0b', wipLimit: 0, description: 'Client responded', subStages: ['Review response', 'Answer questions', 'Schedule call / visit'] },
  { id: 'negotiation', name: 'Negotiation', color: '#ec4899', wipLimit: 0, description: 'Terms under negotiation', subStages: ['Discuss pricing', 'Handle objections', 'Agree final scope'] },
  { id: 'quotation', name: 'Quotation', color: '#a855f7', wipLimit: 0, description: 'Quotation issued', subStages: ['Build quotation', 'Send quotation', 'Follow up'] },
  { id: 'won', name: 'Won', color: '#10b981', wipLimit: 0, description: 'Won — certificate to be generated', subStages: ['Confirm PO / payment', 'Hand over to operations'] },
  // Karim, 16 Aug: a NEW client that does not buy is LOST. An EXISTING client who
  // declines a renewal is REJECTED (see the decision modal). Same event, different
  // word, and sales uses the words precisely — so the board must too.
  { id: 'lost', name: 'Lost', color: '#ef4444', wipLimit: 0, description: 'Lead lost — reason required', subStages: ['Log reason', 'Plan future follow-up'] }
]

export const TICKET_STAGES = [
  { id: 'open', name: 'Open', color: '#3b82f6', wipLimit: 0, description: 'New support ticket' },
  { id: 'progress', name: 'In Progress', color: '#f59e0b', wipLimit: 0, description: 'Being investigated by team' },
  { id: 'waiting', name: 'Waiting on Client', color: '#ec4899', wipLimit: 0, description: 'Awaiting client input' },
  { id: 'resolved', name: 'Resolved', color: '#10b981', wipLimit: 0, description: 'Issue resolved' },
  { id: 'closed', name: 'Closed', color: '#64748b', wipLimit: 0, description: 'Ticket closed' }
]

export const RENEWED_REASONS = [
  'Price Accepted',
  'Service Quality Satisfactory',
  'Competitor Countered',
  'Regular Customer Sync'
]

// An existing client who declines a renewal — "Rejected" in Karim's vocabulary.
export const NOT_RENEWED_REASONS = [
  'Price too high',
  'Competitor selected',
  'Business closed',
  'No longer needs the certificate',
  'Certificate obtained elsewhere',
  'No response',
  'Other'
]

/**
 * A NEW client (a lead) that does not buy — "Lost".
 * Kept as its own list on purpose: you lose a lead for different reasons than an
 * existing client declines a renewal, and merging the two would make the analysis
 * useless. Both are required, both feed GET crm/outcomes.
 */
export const LOST_REASONS = [
  'Price too high',
  'Competitor selected',
  'No budget',
  'No response',
  'Not the right service for them',
  'Timing — postponed',
  'Unqualified lead',
  'Other'
]

// Karim, 7 Sep: "concern, which means the client have concern." Reuses the
// same labels mechanism (already filterable server-side via JSON_CONTAINS)
// instead of a new column.
export const CONCERN_LABEL = { name: 'Concern', color: '#dc2626' }

export const DEFAULT_LABELS = [
  { name: 'Urgent', color: '#ef4444' },
  CONCERN_LABEL,
  { name: 'Important', color: '#f59e0b' },
  { name: 'Follow-up', color: '#3b82f6' },
  { name: 'VIP Client', color: '#a855f7' },
  { name: 'New Account', color: '#10b981' },
  { name: 'Needs Review', color: '#ec4899' },
  { name: 'Escalated', color: '#f97316' },
  { name: 'On Hold', color: '#64748b' }
]

export const PRIORITY_OPTIONS = ['High', 'Medium', 'Low']

export const DEFAULT_BOARD_SETTINGS = {
  density: 'comfortable',
  showLabels: true,
  showDueDates: true,
  showAssignees: true,
  showValues: true,
  cardAging: false,
  cardAgingDays: 14,
  defaultSort: 'date'
}

export const DEFAULT_FILTERS = {
  searchQuery: '',
  stages: [],
  priorities: [],
  owners: [],
  labels: [],
  dateRange: { from: null, to: null },
  hasChecklist: null,
  hasAttachments: null
}

// MID-CRM P1.2 — MOCK_TEAM_MEMBERS (Mohammed, Sara, Khaled, Layla, Omar, Ahmed,
// IT Support, Finance Desk) was DELETED. Those names matched no user in the
// database, so anything assigned from that list could never be saved, and the
// board's owner filter could never match a real card.
// The real team comes from `GET crm/team`. Do not reintroduce a hardcoded list.
